/**
 * Is a turn running in this conversation RIGHT NOW, when the client didn't start it?
 *
 * The harness stamps `pendingPredictionId` on the conversation for the duration of every gateway
 * iteration and clears it when the turn settles, republishing the entity on the CONVERSATION
 * duplexer channel each time. That makes it the one signal a client can read for a turn it did not
 * initiate — a pack accept, a pulse, a brief, an agent report. Until this existed, every client
 * derived "busy" purely from its OWN send, so a server-started turn rendered as a dead screen: the
 * pack-accept report on 2026-08-07 was ~15s of silence followed by a push, which read as a failure.
 *
 * Shared 1:1 with iOS `TurnActivity.swift` and Android `TurnActivity.kt`; the three test tables are
 * the same cases. Change the rule here and change it there.
 */

/**
 * How long a stamped `pendingPredictionId` is believed without a fresh bump.
 *
 * The stamp is cleared by the turn that set it, so a turn that dies uncleanly (pod restart, a
 * gateway callback that never lands) leaves it set forever. An indicator with no end is worse than
 * no indicator — it tells the user something is coming that never will — so the signal expires.
 * Every iteration rewrites the conversation and bumps `updatedDate`, and iterations are seconds
 * apart, so this only has to outlast the slowest single iteration, not the whole turn.
 */
export const TURN_STALE_AFTER_MS = 5 * 60 * 1000;

export interface TurnActivitySignal {
  /** The conversation's `pendingPredictionId` — null/empty when no turn is in flight. */
  pendingPredictionId?: string | null;
  /** The conversation's `updatedDate`; an unparseable or absent value reads as stale. */
  updatedDate?: string | Date | null;
}

/**
 * Should a freshly-arrived CONVERSATION frame be applied, given the newest one already applied?
 *
 * The duplexer does NOT guarantee ordering, and the harness publishes the conversation entity on every
 * write — including writes that carry NO stamp while a turn is live. Two such frames were observed 0.5s
 * apart in production (2026-08-07): `updatedDate 12:15:21.956` with a null `pendingPredictionId`, then
 * `12:15:22.303` carrying the id. Applying the older one last clears the indicator mid-turn.
 *
 * So a frame is applied only when it is at least as new as the last applied one. Equal timestamps pass:
 * two writes can share a millisecond, and dropping the second could lose a real clear.
 */
export function shouldApplyFrame(frameUpdatedAt: number | null, lastAppliedAt: number | null): boolean {
  if (frameUpdatedAt === null) {
    return false;
  }
  return lastAppliedAt === null || frameUpdatedAt >= lastAppliedAt;
}

/** Milliseconds for a signal's `updatedDate`, or null when absent/unparseable. */
export function signalUpdatedAt(signal: TurnActivitySignal | null | undefined): number | null {
  return toMillis(signal?.updatedDate);
}

/** True when the conversation carries a live, non-expired pending turn. */
export function turnActive(signal: TurnActivitySignal | null | undefined, now: number = Date.now()): boolean {
  if (!signal?.pendingPredictionId) {
    return false;
  }
  const updatedMs = toMillis(signal.updatedDate);
  if (updatedMs === null) {
    return false;
  }
  // A clock-skewed future timestamp is still a live turn — only the past can go stale.
  return now - updatedMs < TURN_STALE_AFTER_MS;
}

function toMillis(value: string | Date | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}
